/**
 * When a client re-mints its session token (PRTL-1356 sliding window, PRTL-1519).
 *
 * Tokens live 7 days and are re-minted once they are a day old. "A day old" used to be
 * `Date.now() - iat`: the server's issue time against the device's clock. On a device whose
 * clock runs more than a day fast, every freshly minted token already looked a day old, so
 * the web app refreshed again the moment the new token landed, in a loop, each round
 * creating a session and revoking the last (and racing in-flight requests into 401s).
 *
 * Now the age is measured on the device's own clock from when it RECEIVED the token, so a
 * skewed clock cancels out; the server's `iat` is only the fallback for a token whose
 * receipt time is unknown (one saved before this change). And whatever the clocks say,
 * refresh attempts are at least REFRESH_MIN_INTERVAL_MS apart.
 */

/** Re-mint once the current token is a day old (tokens live 7 days). */
export const REFRESH_AFTER_MS = 24 * 60 * 60 * 1000;

/** Never try to refresh more often than this, whatever the clocks say. */
export const REFRESH_MIN_INTERVAL_MS = 5 * 60 * 1000;

/** The token's `iat` in ms (server clock), or null when it can't be read. */
export function tokenIssuedAtMs(token: string): number | null {
    try {
        // JWT payloads are base64url — convert to base64 and pad before atob.
        const b64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
        const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
        const payload = JSON.parse(atob(padded)) as { iat?: unknown };
        return typeof payload.iat === "number" ? payload.iat * 1000 : null;
    } catch {
        return null;
    }
}

export interface RefreshClock {
    /** The device clock now (ms). */
    now: number;
    /** The device clock when this token was received, or null if unknown. */
    receivedAt: number | null;
    /** The device clock at the last refresh attempt in this session, or null. */
    lastAttemptAt: number | null;
}

/** Whether `token` should be re-minted now. */
export function isRefreshDue(
    token: string,
    { now, receivedAt, lastAttemptAt }: RefreshClock,
): boolean {
    // Debounce: an attempt in the last few minutes (by this clock) blocks another. A clock
    // that has since gone backwards doesn't block for ever.
    if (lastAttemptAt !== null) {
        const since = now - lastAttemptAt;
        if (since >= 0 && since < REFRESH_MIN_INTERVAL_MS) return false;
    }
    if (receivedAt !== null) {
        // The clock went back since the token arrived: re-anchor with one refresh rather
        // than wait out a span that may outlast the token.
        if (receivedAt > now) return true;
        return now - receivedAt >= REFRESH_AFTER_MS;
    }
    const issuedAt = tokenIssuedAtMs(token);
    return issuedAt !== null && now - issuedAt >= REFRESH_AFTER_MS;
}
